import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Maximize2, Share2, Volume2, VolumeX } from 'lucide-react';
import {
  HOSPITALITY_REEL,
  HOSPITALITY_REEL_FRAME_MAX_WIDTH,
  getHospitalityReelSharePath,
  getHospitalityReelSources,
} from '../../config/hospitalityReel';

interface NavigatorWithConnection extends Navigator {
  connection?: { saveData?: boolean };
}

interface WindowWithIdle extends Window {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
}

const canAutoplay = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  return !(navigator as NavigatorWithConnection).connection?.saveData;
};

export const HospitalityReel: React.FC = () => {
  const { t, i18n } = useTranslation('resto');
  const sources = useMemo(() => getHospitalityReelSources(i18n.language), [i18n.language]);
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [idle, setIdle] = useState(false);
  const [near, setNear] = useState(false);
  const [muted, setMuted] = useState(true);
  const [copied, setCopied] = useState(false);
  const [autoplay] = useState(canAutoplay);

  useEffect(() => {
    const w = window as WindowWithIdle;
    const ready = () => setIdle(true);
    const start = () => {
      if (typeof w.requestIdleCallback === 'function') {
        const id = w.requestIdleCallback(ready, { timeout: HOSPITALITY_REEL.idleTimeoutMs });
        return () => w.cancelIdleCallback?.(id);
      }
      const timer = window.setTimeout(ready, HOSPITALITY_REEL.idleTimeoutMs);
      return () => window.clearTimeout(timer);
    };
    if (document.readyState === 'complete') return start();
    let cancel: (() => void) | undefined;
    const onLoad = () => {
      cancel = start();
    };
    window.addEventListener('load', onLoad, { once: true });
    return () => {
      window.removeEventListener('load', onLoad);
      cancel?.();
    };
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || near || !idle) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: HOSPITALITY_REEL.lazyRootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, idle]);

  useEffect(() => {
    const video = videoRef.current;
    if (!near || !video) return;
    video.load();
  }, [near, sources]);

  useEffect(() => {
    const el = sectionRef.current;
    const video = videoRef.current;
    if (!near || !el || !video) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && autoplay) {
          video.play().catch(() => undefined);
        } else if (!entry.isIntersecting) {
          video.pause();
        }
      },
      { threshold: HOSPITALITY_REEL.playThreshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, autoplay]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  const toggleSound = useCallback(() => {
    const video = videoRef.current;
    const next = !muted;
    setMuted(next);
    if (video && !next) {
      video.muted = false;
      video.play().catch(() => {
        video.muted = true;
        setMuted(true);
        video.play().catch(() => undefined);
      });
    }
  }, [muted]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), HOSPITALITY_REEL.shareFeedbackMs);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const shareTitle = t('reel.shareTitle', {
    defaultValue: 'Komby Hospitality — the all-in-one platform for restaurants, hotels and bars',
  });

  const shareVideo = useCallback(async () => {
    const url = `${window.location.origin}${getHospitalityReelSharePath(i18n.language)}`;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: shareTitle, url });
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(url)}`;
    }
  }, [i18n.language, shareTitle]);

  const soundLabel = muted
    ? t('reel.unmute', { defaultValue: 'Turn sound on' })
    : t('reel.mute', { defaultValue: 'Turn sound off' });
  const shareLabel = t('reel.share', { defaultValue: 'Share the video' });
  const reelLabel = t('reel.label', { defaultValue: 'Komby Hospitality video' });
  const watchPath = getHospitalityReelSharePath(i18n.language);
  const controlClass =
    'flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full bg-black/60 border border-white/20 backdrop-blur-sm hover:bg-black/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70';

  return (
    <section
      ref={sectionRef}
      className="relative w-full px-4 py-8 md:py-12"
      aria-label={reelLabel}
    >
      <div
        className="relative mx-auto w-full max-w-6xl aspect-video overflow-hidden rounded-2xl md:rounded-3xl border-[6px] md:border-[10px] border-black bg-black ring-1 ring-white/10 shadow-2xl shadow-black/50"
        style={{ maxWidth: HOSPITALITY_REEL_FRAME_MAX_WIDTH }}
      >
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-contain"
          muted
          loop
          playsInline
          preload="none"
          controls={!autoplay}
          poster={near ? sources.poster : undefined}
        >
          {near ? (
            <>
              <source src={sources.webm} type="video/webm" />
              <source src={sources.mp4} type="video/mp4" />
            </>
          ) : null}
        </video>
        <div className="absolute bottom-3 left-3 md:bottom-5 md:left-5 z-10 flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={toggleSound}
            aria-label={soundLabel}
            aria-pressed={!muted}
            title={soundLabel}
            className={controlClass}
          >
            {muted ? (
              <VolumeX className="w-5 h-5 md:w-6 md:h-6 text-red-500" aria-hidden="true" />
            ) : (
              <Volume2 className="w-5 h-5 md:w-6 md:h-6 text-green-500" aria-hidden="true" />
            )}
          </button>
          <button type="button" onClick={shareVideo} aria-label={shareLabel} title={shareLabel} className={controlClass}>
            {copied ? (
              <Check className="w-5 h-5 md:w-6 md:h-6 text-green-500" aria-hidden="true" />
            ) : (
              <Share2 className="w-5 h-5 md:w-6 md:h-6 text-white" aria-hidden="true" />
            )}
          </button>
          <a href={watchPath} aria-label={reelLabel} title={reelLabel} className={controlClass}>
            <Maximize2 className="w-5 h-5 md:w-6 md:h-6 text-white" aria-hidden="true" />
          </a>
          <span
            role="status"
            aria-live="polite"
            className={`px-3 py-1.5 rounded-full bg-black/70 text-sm text-white transition-opacity ${copied ? 'opacity-100' : 'opacity-0'}`}
          >
            {copied ? t('reel.shareCopied', { defaultValue: 'Link copied' }) : ''}
          </span>
        </div>
      </div>
    </section>
  );
};
